import { Anchor, Box, Container, Stack, Text } from '@mantine/core';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';

import type { FounderLetter } from '../../components/marketing/FounderLetterCard';
import { MarkdownContent } from '../../components/marketing/MarkdownContent';
import { MarketingHero } from '../../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../../components/marketing/MarketingSection';
import { PublicShell } from '../../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../../lib/cms';

interface ApiLetter {
  slug: string;
  label: string;
  title: string;
  excerpt: string;
  body: string;
  author: string;
  published_at: string;
}

function fromApi(row: ApiLetter): FounderLetter {
  return {
    slug: row.slug,
    label: row.label,
    title: row.title,
    excerpt: row.excerpt,
    body: row.body,
    author: row.author,
    publishedAt: row.published_at,
  };
}

export const Route = createFileRoute('/ripples/$slug')({
  loader: async ({ params }) => {
    const res = await fetch(
      `/api/__public__/notes-from-the-road/${encodeURIComponent(params.slug)}`,
    );
    if (res.status === 404) throw notFound();
    if (!res.ok) throw notFound();
    const data = (await res.json()) as { letter: ApiLetter };
    return { letter: fromApi(data.letter) };
  },
  component: NoteSlugPage,
  notFoundComponent: NoteNotFound,
});

function NoteNotFound() {
  const eyebrow = useCmsSlot<'markdown'>('notes_from_the_road.notfound_hero_eyebrow');
  const title = useCmsSlot<'markdown'>('notes_from_the_road.notfound_hero_title');
  const lede = useCmsSlot<'markdown'>('notes_from_the_road.notfound_hero_lede');
  const backLink = useCmsSlot<'markdown'>('notes_from_the_road.notfound_back_link');

  return (
    <PublicShell>
      <MarketingHero eyebrow={eyebrow} title={title} lede={lede} />
      <MarketingSection>
        <Anchor component={Link} to="/ripples" c="brand.7" fw={500}>
          {backLink}
        </Anchor>
      </MarketingSection>
    </PublicShell>
  );
}

function NoteSlugPage() {
  const { letter } = Route.useLoaderData();
  const { image: heroImg, ready: heroImgReady } = useCmsImage('notes_from_the_road.hero_image');
  const keepTitle = useCmsSlot<'markdown'>('notes_from_the_road.keep_title');
  const keepLede = useCmsSlot<'markdown'>('notes_from_the_road.keep_lede');

  const date = new Date(letter.publishedAt).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={`${letter.label} · ${date}`}
        title={letter.title}
        lede={letter.excerpt}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.18}
      />

      <Box
        component="section"
        style={{
          paddingBlock: 'clamp(48px, 6vw, 80px)',
          backgroundColor: 'light-dark(#ffffff, var(--mantine-color-dark-8))',
        }}
      >
        <Container size="md">
          <Stack gap="xl" maw={720}>
            <MarkdownContent body={letter.body} />
            <Box
              style={{
                paddingTop: 24,
                borderTop: '1px solid var(--mantine-color-ink-1)',
              }}
            >
              <Text size="sm" c="ink.6" style={{ letterSpacing: '0.01em' }}>
                {letter.author}
              </Text>
            </Box>
          </Stack>
        </Container>
      </Box>

      <MarketingNextSteps
        title={keepTitle}
        lede={keepLede}
        destinations={['/ripples', '/how-it-works', '/founding-venues']}
      />
    </PublicShell>
  );
}
